import React, { useEffect, useState } from 'react';
import { Icon } from '@shakkerai/components';
import { useTempState } from '@starflow/app/store';
import { updateLoraSegState, updatePartialState, useCategories, usePartialState } from '../../store/state';
import { getTagCodesV2, queryCategories, queryLoraSegState, queryModelState } from '../../store/actions';
import { Filter } from '../base/Filter';
import { translate } from '@shakkerai/util-i18n';
import { useAsyncEffect } from 'ahooks';
import { findClassConfig } from '@shakkerai/pro-models/src/constants/toolCode';
import { ClassificationList } from '../base/ClassificationList';
import { MODEL_FLUX } from '../../constants';
import LoraLabel, { LABEL_LEVEL } from '../base/Label';
import { BestModelList } from '../base/BestModelList';
import { ModelList } from '../base/List';

const sortOptions = [
  { value: '0', label: translate('common_sort1') },
  { value: '2', label: translate('common_sort3') },
];

const cardSize = { width: 185, height: 272 };
export const AllLoras = React.memo(() => {
  const topKeyword = usePartialState('keyword');
  const globaoMode = useTempState('globalMode');

  // const { filters, data, checkpointRelatedItems, promptRelatedItems } = usePartialState('lora.all');
  const { filters, data } = usePartialState('lora.all');
  const segments = usePartialState('lora.segments');
  const [segLoading, setSegLoading] = useState(false);
  const categories = useCategories((state) => state.lora);
  const handleApplyFilter = async (params, isGetList = false) => {
    const newParams = { ...filters, ...params };
    newParams.sortType === '2' || topKeyword.value || isGetList || newParams.label2Code
      ? await getListData(newParams)
      : await getRecommendData(newParams);
  };

  const getListData = async (params) => {
    if (globaoMode === 'simple') {
      params.baseTypes = [`${MODEL_FLUX}`];
    }
    if (params.sortType === '2') {
      params.combinedTagCodes = undefined;
    }
    // 筛选项变更，需要重置分页和现有内容
    updatePartialState('lora.all.data', { items: [], total: 0, hasMore: true });
    updatePartialState('lora.all.filters', { ...filters, ...params, page: 1 });
    queryModelState('lora', 'all');
  };

  const handleCancelSearch = (event) => {
    updatePartialState('keyword', { value: '' });
    handleApplyFilter({ keyword: '' });
    event.stopPropagation();
  };

  const handleScroll = (event) => {
    if (!data.hasMore || data.loading) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      updatePartialState('lora.all.filters', {
        page: filters.page + 1,
        baseTypes: globaoMode === 'simple' ? [`${MODEL_FLUX}`] : filters.baseTypes,
      });
      queryModelState('lora', 'all');
    }
  };

  useEffect(() => {
    queryCategories('lora');
  }, []);

  useAsyncEffect(async () => {
    // 根据底模去判断是基础模型还是预设模型，如果是预设模型，需要定位到对应分类去获取数据展示
    const v2ids = await getTagCodesV2();
    const fs = {
      ...filters,
      category: topKeyword.value ? '0' : v2ids.length ? findClassConfig(v2ids).codes ?? '0' : '0',
      label2Code: undefined,
      sortType: topKeyword.value ? undefined : filters.sortType,
    };
    updatePartialState('lora.all.filters', fs);
    if (!categories.featuredCates.length) return;
    await getData(fs, categories);
  }, [topKeyword.value, categories]);

  const getData = async (filters, categories) => {
    if (topKeyword.value) {
      await getListData({ ...filters, keyword: topKeyword.value, combinedTagCodes: undefined });
    } else {
      const ps = {
        ...filters,
        keyword: '',
        sortType: filters.category === '0' ? undefined : '0',
        baseTypes: globaoMode === 'simple' ? [`${MODEL_FLUX}`] : filters.category === '0' ? [] : filters.baseTypes,
      };
      updatePartialState('lora.all.filters', ps);
      if (filters.category === '0') {
        await getLevel1Top(categories.featuredCates, ps);
      } else {
        // 退出搜索模式
        await getRecommendData(ps);
      }
    }
  };

  // 编辑精选
  const getLevel1Top = async (arr, ps) => {
    // 如果匹配是他，就是走featuredCates配置 特例
    const res = [];
    for (const con of arr) {
      if (con.displayCount > 0) {
        const params = {
          ...ps,
          tagCode: con.categoryCode,
          pageSize: con.displayCount,
          combinedTagCodes: con.includedTags,
        };
        res.push(() => queryLoraSegState(`0.${con.includedTags}`, params, con));
      }
    }
    await Promise.all(res.map((f) => f()));
  };

  // 一级tag（除开编辑精选）
  const getLevel1Other = async (category, params) => {
    const res = [];
    const arr = categories.catalogArr.find((cata) => cata.code === category);
    const target = arr.level2 || [];
    for (const con of target) {
      if (con.displayCount > 0) {
        const ps = {
          ...params,
          tagCode: con.categoryCode,
          pageSize: con.displayCount,
          baseTypes: globaoMode === 'simple' ? [`${MODEL_FLUX}`] : params.baseTypes,
          combinedTagCodes: con.includedTags,
        };
        const statePath = `${category}.${con.includedTags}`;
        updateLoraSegState(statePath, [], con);
        res.push(() => queryLoraSegState(statePath, ps, con));
      }
    }
    await Promise.all(res.map((f) => f()));
  };

  const getRecommendData = async (newParams) => {
    setSegLoading(true);
    updatePartialState('lora.all.filters', newParams);
    const { category } = newParams;
    if (category === '0') {
      await getLevel1Top(categories.featuredCates);
    } else {
      await getLevel1Other(category, newParams);
    }
    setSegLoading(false);
  };

  const renderContent = () => {
    /**
     * 展示list条件：or
     * 二级标签选中 top类型 输入框（两处）有值
     */
    if (filters.label2Code || filters.sortType === '2' || filters.keyword || topKeyword.value) {
      if (data.loading && filters.page === 1) {
        return (
          <div className="flex items-center justify-center mt-32 text-xs text-[#7f7f7f]">{translate('loading')}...</div>
        );
      }
      if (data.items.length) {
        if (filters.keyword || topKeyword.value) {
          return (
            <ModelList
              data={data}
              cardSize={cardSize}
              cols={4}
              showTag={!filters.keyword && !topKeyword.value}
              query={filters.keyword || topKeyword.value}
              withHotBadge={filters.sortType === '2' && !filters.keyword && !topKeyword.value}
              withAuthorInfo
            />
          );
        } else {
          return (
            <BestModelList
              data={data}
              cardSize={cardSize}
              showTag={!filters.keyword && !topKeyword.value}
              query={filters.keyword || topKeyword.value}
              cols={4}
              withHotBadge={filters.sortType === '2' && !filters.keyword && !topKeyword.value}
              withAuthorInfo
            />
          );
        }
      } else {
        return (
          <div className="flex items-center justify-center mt-32 text-xs text-[#7f7f7f]">
            {translate('model_nosearch')}
          </div>
        );
      }
    } else {
      const target = segments && segments[filters.category];
      if (!target || segLoading) {
        return (
          <div className="flex items-center justify-center mt-32 text-xs text-[#7f7f7f]">{translate('loading')}...</div>
        );
      }
      return (
        <ClassificationList
          handleApplyFilter={(e) => justifyBehavior(e)}
          data={target}
          cardSize={cardSize}
          cols={4}
          showTag={!filters.keyword && !topKeyword.value}
          filters={filters}
          withHotBadge={false}
          withAuthorInfo
        />
      );
    }
  };

  const getLevel2Labels = () => {
    if (topKeyword.value) {
      return categories.catalogArr.slice(1);
    }
    if (filters.category === '0') {
      return categories.featuredCates;
    } else {
      const cate = categories.catalogArr.find((ca) => ca.code === filters.category);
      return cate ? cate.level2 : [];
    }
  };

  const justifyBehavior = ({ includedTags, categoryCode, whichLevel, code }) => {
    if (whichLevel === LABEL_LEVEL.l1) {
      const l = filters.label2Code === code ? undefined : code;
      handleApplyFilter(
        {
          category: l ? code : '0',
          sortType: topKeyword.value ? undefined : '0',
          label2Code: l,
        },
        Boolean(code),
      );
    } else {
      const newCode = filters.label2Code === includedTags ? null : includedTags;
      handleApplyFilter(
        {
          label2Code: newCode,
          category: categoryCode,
          sortType: topKeyword.value ? undefined : '0',
          combinedTagCodes: newCode ? includedTags : undefined,
        },
        Boolean(newCode),
      );
    }
  };

  let level2LabelList = getLevel2Labels();
  level2LabelList = level2LabelList?.filter((item) => item.alias !== '星流独家');

  return (
    <main className="flex items-stretch flex-1 overflow-hidden">
      <aside className="flex flex-col items-stretch w-[150px] p-2 space-y-1 border-r border-[#efefef]">
        {topKeyword.value || filters.keyword ? (
          <div className="flex items-center">
            <button onClick={handleCancelSearch} className="flex items-center w-4 h-4 rotate-180">
              <Icon className="icon-pro_arrow_right" fontSize={14} color="#7F7F7F" />
            </button>
            <span className="ml-2 translate-y-px text-[13px] font-normal text-[#2D2D2D]">
              {translate('model_search')}
              {data.loading ? null : (
                <span className="text-[8C8C8C]">
                  ({data.total >= 1000 ? `${parseInt(data.total / 1000)}k+` : data.total})
                </span>
              )}
            </span>
          </div>
        ) : (
          categories.catalogArr.map((item) => (
            <button
              key={item.code}
              disabled={filters.category === item.code}
              onClick={() =>
                handleApplyFilter(
                  { category: item.code, label2Code: undefined, sortType: item.code === '0' ? undefined : '0' },
                  false,
                )
              }
              className="flex items-center py-[8px] px-3 rounded text-[13px] leading-[18px] whitespace-normal break-words text-left overflow-hidden text-[#7f7f7f] hover:bg-[#F4F6F8] disabled:bg-primary/15 disabled:text-[#2d2d2d] disabled:font-semibold"
            >
              {item.tagLabel}
            </button>
          ))
        )}
      </aside>
      <div className="flex flex-col flex-1 overflow-hidden">
        <>
          <div className="relative z-[5] px-5 py-2" style={{ boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.05)' }}>
            <Filter
              from="all"
              value={filters}
              onSubmit={(params) => handleApplyFilter(params)}
              sortOptions={filters.category === '0' ? [] : sortOptions}
            >
              {level2LabelList?.map((item, idx) => (
                <LoraLabel
                  data={item}
                  key={idx}
                  label2Code={filters.label2Code}
                  chooseLabel={(obj) => justifyBehavior(obj)}
                  filters={filters}
                />
              ))}
            </Filter>
          </div>
          <div onScroll={handleScroll} className="flex-1 px-5 overflow-auto tiny-scrollbar bg-[#f9fbfb]">
            {renderContent()}
          </div>
        </>
      </div>
    </main>
  );
});
